<template>
  <BackofficeLayout
    :title="$t('message.registration_form.set_up_application_form')"
    :loading="loading"
  >
    <div class="wrapper">
      <div class="main">
        <v-sheet elevation="0" class="radius-10px">
          <div class="form-row form-row-gap--xl">
            <div class="form-row form-row-gap--sm mb-0">
              <v-text-field
                v-model="keyword"
                outlined
                :placeholder="
                  $t('message.registration_form.search_by_academic_year')
                "
                prepend-inner-icon="mdi-magnify"
                required
                hide-details
                dense
                style="width: 400px"
              ></v-text-field>

              <v-btn
                v-if="keyword.length > 0"
                color="primary"
                outlined
                elevation="0"
                @click="search"
                width="90"
              >
                {{ $t("message.common.search") }}
              </v-btn>
              <v-btn
                v-else
                color="gray"
                outlined
                elevation="0"
                @click="search"
                width="90"
              >
                {{ $t("message.common.search") }}
              </v-btn>
            </div>
            <v-select
              v-model="selectedFormStatus"
              @change="changeFormStatus"
              outlined
              :items="formStatus"
              :item-text="`label_${$i18n.locale}`"
              item-value="value"
              prepend-inner-icon="mdi-filter-variant"
              hide-details
              dense
              style="width: 240px"
            ></v-select>
            <div class="form-row form-row--full form-row--end">
              <v-btn
                class="text-right"
                color="primary"
                elevation="0"
                rounded
                @click="goToRegistrationFormAdd"
                width="180"
              >
                <v-icon class="icon--white" left> mdi-plus </v-icon>
                {{ $t("message.registration_form.application_form") }}
              </v-btn>
            </div>
          </div>
        </v-sheet>

        <v-sheet rounded elevation="1" class="radius-10px">

          <v-data-table
            class="custom-data-table"
            @update:items-per-page="handleChangePageSize"
            @update:page="handleChangePage"
            :page.sync="page"
            :options.sync="options"
            :headers="headers"
            :items="registrationForm"
            :server-items-length="total"
            :items-per-page="pageSize"
            :footer-props="{
              'page-text': `${pageSizeText} ${$t(
                'message.common.pagination.of'
              )} ${total}`,
              'items-per-page-text': `${$t(
                'message.common.pagination.row_per_page'
              )}:`,
              'items-per-page-options': pageSizeOption,
            }"
            hide-default-header
            :hide-default-footer="registrationForm <= 0"
          >
            <template v-slot:header="{ props: { headers } }">
              <thead>
                <tr>
                  <th
                    v-for="item in headers"
                    :key="item.value"
                    class="column px-3"
                    :style="{ width: `${item.width}` }"
                  >
                    <div
                      v-if="item.sortable"
                      class="custom-table-header"
                      @click="handleChangeSort(item.sort)"
                    >
                      {{ item.text }}
                      <template v-if="item.sort === sort">
                        <v-icon v-if="sortDirection === 'desc'" small
                          >mdi-chevron-up</v-icon
                        >
                        <v-icon v-else small>mdi-chevron-down</v-icon>
                      </template>
                      <template v-else>
                        <v-icon small>mdi-unfold-more-horizontal</v-icon>
                      </template>
                    </div>
                    <div v-else>
                      {{ item.text }}
                    </div>
                  </th>
                </tr>
              </thead>
            </template>
            <template v-slot:[`item.form_status`]="{ item }">
              <span class="text--error" v-if="item.form_status === 'DRAFT'">{{
                formStatusText(item.form_status)
              }}</span>
              <span v-else>
                <v-icon
                  v-if="item.form_status === 'ACTIVE'"
                  left
                  class="icon--green"
                  small
                  >mdi-check-circle</v-icon
                >
                {{ formStatusText(item.form_status) }}
              </span>
            </template>
            <template v-slot:[`item.opening_date`]="{ item }">
              {{ formatDateTime(item.opening_date) }} -
              {{ formatDateTime(item.closing_date) }}
            </template>
            <template v-slot:[`item.total_plans`]="{ item }">
              {{ item.total_plans || 0 }}
            </template>
            <template v-slot:[`item.total_students`]="{ item }">
              {{ item.total_students || 0 }}
            </template>
            <template v-slot:[`item.updated_at`]="{ item }">
              {{ formatDateTime(item.updated_at) }}
            </template>
            <template v-slot:[`item.action`]="{ item }">
              <div class="custom-table-action">
                <template v-if="item.form_status !== 'DRAFT'">
                  <router-link
                    class="icon--link"
                    :to="`/registration/form/${item.id}/view`"
                    ><v-icon small>mdi-eye</v-icon></router-link
                  >
                </template>
                <template v-else>
                  <v-icon class="icon--disabled" small>mdi-eye</v-icon>
                </template>
                <template
                  v-if="
                    item.form_status === 'DRAFT' || item.form_status === 'WAIT'
                  "
                >
                  <router-link
                    class="icon--link"
                    :to="`/registration/form/${item.id}/edit`"
                    ><v-icon small>mdi-pencil</v-icon></router-link
                  >
                </template>
                <template v-else>
                  <v-icon class="icon--disabled" small>mdi-pencil</v-icon>
                </template>

                <template v-if="item.form_status === 'ACTIVE'">
                  <v-icon class="icon--disabled" small>mdi-delete</v-icon>
                </template>
                <template v-else>
                  <v-icon small @click="openDeleteConfirmationModal(item)"
                    >mdi-delete</v-icon
                  >
                </template>
              </div>
            </template>
            <template v-slot:no-data>
              <div class="custom-data-table-empty-data">
                <div>
                  <v-icon class="mb-2" large>mdi-file-document</v-icon>
                  <p style="white-space: pre-line">
                    {{
                      $t(
                        "message.registration_form.please_create_an_online_application_form"
                      )
                    }}
                  </p>
                </div>
              </div>
            </template>
          </v-data-table>
        </v-sheet>
      </div>
    </div>
    <v-dialog v-model="isShowDeleteConfirmationModal" width="450" class="radius-10px">
      <div class="modal__content">
        <v-icon
          class="modal__icon modal__close-button"
          @click="closeDeleteConfirmationModal"
          >mdi-close</v-icon
        >
        <v-icon class="modal__icon modal__icon__red">mdi-alert-circle</v-icon>
        <p class="modal__title">
          {{ $t("message.registration_form.remove_selected_application_form") }}
          ?
        </p>
        <p class="modal__description" v-if="selectedRegistrationForm">
          {{
            $t("message.registration_form.all_information_of_will_disappear", {
              name: selectedRegistrationForm.register_name,
            })
          }}
        </p>
        <div class="modal__action">
          <v-btn
            color="primary"
            outlined
            rounded
            elevation="0"
            width="180"
            :loading="isSubmitting"
            @click="closeDeleteConfirmationModal"
          >
            {{ $t("message.common.cancel") }}
          </v-btn>
          <v-btn
            color="error"
            rounded
            elevation="0"
            width="180"
            :loading="isSubmitting"
            @click="deleteRegistrationForm"
          >
            {{ $t("message.common.confirm") }}
          </v-btn>
        </div>
      </div>
    </v-dialog>
  </BackofficeLayout>
</template>

<script>
import BackofficeLayout from "@/components/common/BackofficeLayout";
import * as services from "@/services";
import * as dateUtils from "@/utils/date";
import { calculateCurrentPage } from "@/utils/pagination";
import {
  FORM_STATUS_OPTION,
  FORM_STATUS_MAPPING,
  PAGE_SIZE_OPTIONS,
  OPPOSITE_SORT,
  DEFAULT_PAGE_SIZE,
} from "@/constants";
import router from "@/router";

const DEFAULT_SORT_KEY = "register_form_config.semester_year";
const DEFAULT_SORT_DIRECTION = "DESC";
export default {
  name: "RegistrationForm",
  components: {
    BackofficeLayout,
  },
  data() {
    return {
      registrationForm: [],
      selectedRegistrationForm: null,
      keyword: "",
      selectedFormStatus: "",
      formStatus: FORM_STATUS_OPTION,
      isShowDeleteConfirmationModal: false,
      isSubmitting: false,
      options: {}, // v-data-table
      pages: 1,
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      total: 0,
      sort: DEFAULT_SORT_KEY,
      sortDirection: DEFAULT_SORT_DIRECTION,
      pageSizeOption: PAGE_SIZE_OPTIONS,
      loading: 0,
    };
  },
  mounted() {
    this.init();
  },
  methods: {
    search() {
      const path = this.$route.path;
      const query = this.prepareQuery();
      router.push({
        path,
        query: { ...query, keyword: this.keyword },
      });
    },
    changeFormStatus(value) {
      const path = this.$route.path;
      const query = this.prepareQuery();
      router.push({
        path,
        query: { ...query, status: value },
      });
    },
    goToRegistrationFormAdd() {
      router.push("/registration/form/add");
    },
    formatDateTime(date) {
      return dateUtils.formatDateTime(date, this.$i18n.locale);
    },
    async getRegistrationForm() {
      try {
        this.loading++;
        const query = this.prepareQuery();
        const {
          data: { data },
        } = await services.getRegistrationForm(query);
        this.registrationForm = data.registration_form;
        this.pages = data.total_page;
        this.total = data.total;
      } catch (error) {
        console.log(error);
      } finally {
        this.loading--;
      }
    },
    async deleteRegistrationForm() {
      try {
        const id = this.selectedRegistrationForm.id;
        this.isSubmitting = true;
        await services.deleteRegistrationStudent(id);
        this.getRegistrationForm();
      } catch (error) {
        console.log(error);
      } finally {
        this.closeDeleteConfirmationModal();
        this.isSubmitting = false;
      }
    },
    formStatusText(status) {
      return FORM_STATUS_MAPPING[status][`text_${this.$i18n.locale}`];
    },
    openDeleteConfirmationModal(data) {
      this.selectedRegistrationForm = data;
      this.isShowDeleteConfirmationModal = true;
    },
    closeDeleteConfirmationModal() {
      this.isShowDeleteConfirmationModal = false;
    },
    handleChangePage(newPage) {
      const query = this.prepareQuery();
      const path = this.$route.path;
      router.push({
        path,
        query: { ...query, page: newPage },
      });
    },
    handleChangePageSize(newPageSize) {
      const query = this.prepareQuery();
      const path = this.$route.path;
      router.push({
        path,
        query: { ...query, page_size: newPageSize },
      });
    },
    handleChangeSort(key) {
      let direction = "desc";
      if (key === this.sort) {
        direction = OPPOSITE_SORT[this.sortDirection] || "desc";
      }
      const sort = `${key}:${direction}`;
      const path = this.$route.path;
      const query = this.prepareQuery();
      router.push({
        path,
        query: { ...query, sort },
      });
    },
    prepareQuery() {
      return {
        keyword: this.keyword,
        status: this.selectedFormStatus,
        page: this.page,
        page_size: this.pageSize,
        sort: `${this.sort}:${this.sortDirection}`,
      };
    },
    init() {
      const query = this.$route.query;
      const keyword = query.keyword || "";
      const status = query.status || "";
      const page = query.page ? parseInt(query.page) : 1;
      const pageSize = query.page_size
        ? parseInt(query.page_size)
        : DEFAULT_PAGE_SIZE;
      const sort = query.sort ? query.sort.split(":") : "";
      const sortKey = sort?.[0] ?? DEFAULT_SORT_KEY;
      const sortDirection = sort?.[1] ?? DEFAULT_SORT_DIRECTION;
      this.keyword = keyword;
      this.selectedFormStatus = status;
      this.page = page;
      this.pageSize = pageSize;
      this.sort = sortKey;
      this.sortDirection = sortDirection;
      this.options.itemsPerPage = this.pageSize;

      this.getRegistrationForm();
    },
  },
  computed: {
    pageSizeText() {
      return calculateCurrentPage(this.page, this.pageSize, this.total)
    },
    headers() {
      return [
        {
          text: this.$t("message.common.academic_year"),
          value: "semester_year",
          sort: "register_form_config.semester_year",
          sortable: true,
          width: "14%",
        },
        {
          text: this.$t("message.registration_form.application_title"),
          value: "register_name",
          sort: "register_form_config.register_name",
          sortable: true,
          width: "15%",
        },
        {
          text: this.$t("message.common.curriculum"),
          value: "total_plans",
          sort: "register_plan.total_plans",
          sortable: true,
          width: "12%",
        },
        {
          text: this.$t("message.registration_form.application_available"),
          value: "total_students",
          sort: "register_plan.total_students",
          sortable: true,
          width: "10%",
        },
        {
          text: this.$t("message.registration_form.application_period"),
          value: "opening_date",
          sort: "register_form_config.opening_date",
          sortable: true,
          width: "20%",
        },
        {
          text: this.$t("message.common.status"),
          value: "form_status",
          sort: "register_form_config.form_status",
          sortable: true,
          width: "15%",
        },
        {
          text: this.$t("message.common.last_edited"),
          value: "updated_at",
          sort: "register_form_config.updated_at",
          sortable: true,
          width: "20%",
        },
        {
          text: "",
          value: "action",
          width: "10%",
        },
      ];
    },
  },
  watch: {
    $route() {
      this.init();
    },
  },
};
</script>

<style scoped>
.wrapper {
  padding: 0 24px 24px 24px;
}

.main {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-top: 24px;
  border-top: 1px solid #d7d6d8;
}


.radius-10px{
  border-radius:10px !important;
}

</style>
